import { styled } from '@mui/material/styles';
import {
    Box,
    Paper,
    Typography,
    Switch,
    TableContainer,
    TableCell,
    TableRow,
    Card
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

export const FilterSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    width: '100%',
    position: 'relative'
}));

export const DateFilterContainer = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 10,
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
        position: 'static',
        marginBottom: theme.spacing(2)
    }
}));

export const ResultsSection = styled(Box)(({ theme }) => ({
    width: '100%'
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    '&::before': {
        content: '""',
        width: '4px',
        height: '24px',
        backgroundColor: theme.palette.primary.main,
        borderRadius: '4px',
        marginRight: theme.spacing(1.5)
    }
}));

export const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
    width: '160px',
    '& .MuiOutlinedInput-root': {
        height: '40px'
    }
}));

export const ButtonContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2)
}));

export const ScrollableTableContainer = styled(TableContainer)(({ theme }) => ({
    maxWidth: '100%',
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
        height: '8px'
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: '#f1f1f1'
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#bbbbbb',
        borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#888888'
    }
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
    whiteSpace: 'nowrap',
    padding: '8px 16px',
    fontSize: '0.875rem',
    borderRight: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
        borderRight: 'none'
    },
    '&.MuiTableCell-head': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        position: 'relative'
    },
    '&.sticky-left': {
        position: 'sticky',
        left: 0,
        zIndex: 1
    },
    '&.sticky-left.MuiTableCell-head': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        zIndex: 102
    },
    '&.sticky-left:not(.MuiTableCell-head)': {
        backgroundColor: theme.palette.background.paper
    }
}));

export const StyledTableRow = styled(TableRow)(() => ({}));

export const TableControlBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
}));

export const CustomSwitch = styled(Switch)(({ theme }) => ({
    width: 68,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
        margin: 1,
        padding: 0,
        '&.Mui-checked': {
            transform: 'translateX(32px)',
            color: theme.palette.primary.main,
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.primary.light,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        width: 30,
        height: 30,
        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        backgroundColor: '#fff',
        border: '1px solid rgba(0,0,0,0.2)',
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        borderRadius: 20 / 2,
        backgroundColor: theme.palette.grey[400],
    },
}));

export const SwitchLabel = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
}));

export const SwitchTextOn = styled(Typography)(({ theme, checked }) => ({
    position: 'absolute',
    left: '14px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    color: '#fff',
    visibility: checked ? 'visible' : 'hidden',
}));

export const SwitchTextOff = styled(Typography)(({ theme, checked }) => ({
    position: 'absolute',
    right: '10px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    color: '#fff',
    visibility: checked ? 'hidden' : 'visible',
}));

export const ColumnSelectionPanel = styled(Paper)(({ theme }) => ({
    position: 'absolute',
    top: 'calc(100% + 10px)',
    right: 0,
    width: '500px',
    maxWidth: '90vw',
    zIndex: 100,
    padding: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    borderRadius: theme.shape.borderRadius,
    opacity: 0,
    transform: 'translateY(-20px)',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    // 닫힌 상태(opacity:0)에서도 박스가 DOM에 남아 테이블 클릭을 가로채지 않도록
    pointerEvents: 'none',
    '&.open': {
        opacity: 1,
        transform: 'translateY(0)',
        pointerEvents: 'auto'
    }
}));

export const ColumnSelectionGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1.5),
    maxHeight: '300px',
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.grey[100],
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.grey[400],
        borderRadius: '3px',
    },
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
}));

export const ColumnSelectionItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5),
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
    },
    '& .MuiCheckbox-root': {
        padding: '4px',
    },
    '& .MuiTypography-root': {
        fontSize: '0.875rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}));

export const MobileCardItem = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    position: 'relative'
}));

export const MobileCardHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    alignItems: 'center'
}));

export const MobileCardContent = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1)
}));

export const MobileCardField = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column'
}));

export const MobileCardLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
}));

export const MobileCardValue = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    fontWeight: 500
})); 